'use server'

import { apiRequest, ApiResponse } from '../request'

export type AiUsageKind = 'CHAT' | 'EMBEDDING' | 'TRANSCRIPTION'
export type AiUsageScope = 'ORGANIZATION' | 'PLATFORM' | 'UNOWNED'

/** Totais de um recorte. Custo em micro-dólares (US$ 1 = 1.000.000). */
export interface AiUsageTotals {
    calls: number
    unpricedCalls: number
    inputTokens: number
    cachedInputTokens: number
    outputTokens: number
    audioInputTokens: number
    audioSeconds: number
    inputChars: number
    toolResultChars: number
    costUsdMicros: number
    /** Debitado dos tenants (custo com a margem), em milésimos de centavo. */
    chargedMilliCents: number
    /** Parte do debitado sem crédito para cobrir, em milésimos de centavo. */
    uncoveredMilliCents: number
    /** Créditos descontados, em milésimos de crédito. */
    creditMillis: number
}

/** Organização com 80% ou mais do saldo de créditos gasto. */
export interface AiCreditLowBalance {
    organizationId: number
    name: string | null
    grantedCredits: number
    remainingCredits: number
    blocked: boolean
}

export interface AiUsageSummary {
    from: string
    to: string
    usdBrlRate: number
    totals: AiUsageTotals
    byDay: Array<{ day: string } & AiUsageTotals>
    byOrganization: Array<{ organizationId: number; name: string | null } & AiUsageTotals>
    byFeature: Array<{ feature: string } & AiUsageTotals>
    byModel: Array<{ model: string; kind: AiUsageKind } & AiUsageTotals>
    byKind: Array<{ kind: AiUsageKind } & AiUsageTotals>
    byScope: Array<{ scope: AiUsageScope } & AiUsageTotals>
    unownedByFeature: Array<{ feature: string } & AiUsageTotals>
    unpricedModels: Array<{ model: string; calls: number }>
    lowBalance: AiCreditLowBalance[]
}

export interface AiModelPrice {
    model: string
    inputUsdPerMTok: number
    cachedInputUsdPerMTok: number
    outputUsdPerMTok: number
    audioInputUsdPerMTok: number
    usdPerMinute: number
}

export interface AiPricing {
    usdBrlRate: number
    models: AiModelPrice[]
}

export async function getAiUsageSummary(
    from: string,
    to: string
): Promise<ApiResponse<AiUsageSummary>> {
    return apiRequest<AiUsageSummary>('ai-usage/summary', { params: { from, to } })
}

export async function getAiPricing(): Promise<ApiResponse<AiPricing>> {
    return apiRequest<AiPricing>('ai-usage/pricing', {})
}

export async function updateAiPricing(
    data: AiPricing
): Promise<ApiResponse<AiPricing>> {
    return apiRequest<AiPricing>('ai-usage/pricing', { data }, 'put')
}

export interface OpenRouterPriceSuggestion {
    model: string
    inUse: boolean
    current: AiModelPrice | null
    suggested: Pick<AiModelPrice, 'inputUsdPerMTok' | 'cachedInputUsdPerMTok' | 'outputUsdPerMTok'> | null
}

export interface OpenRouterPricePreview {
    fetchedAt: string
    models: OpenRouterPriceSuggestion[]
}

/** Prévia dos preços do OpenRouter. Não grava nada. `models`: nomes do formulário ainda não salvos. */
export async function getOpenRouterPricePreview(
    models: string[]
): Promise<ApiResponse<OpenRouterPricePreview>> {
    return apiRequest<OpenRouterPricePreview>('ai-usage/pricing/openrouter', {
        params: { models: models.join(',') },
    })
}
